import Vue from 'vue';
import axios from 'axios';
import router from '@/routers';
import { facilita } from '@/global';
import store from '@/store';
import { storage } from '@/services/storage';
import { auth } from '@/services/axios';
import helpers from '@/mixins/helpers';
/**
 * Cria uma instância do Axios com uma configuração base
 * @param {string} baseURL - A URL base para as requisições da instância Axios
 * @param {Object} [options={}] - Opções adicionais para configurar a instância Axios
 * @returns {Object} - Uma instância configurada do Axios
 */
const createAxiosInstance = (baseURL, options = {}) => {
  return axios.create({
    baseURL,
    ...options,
  });
};

// Criação das instâncias do Axios
const apiConfig = JSON.parse(storage.get('api_config'));
let cancelTokenSource = axios.CancelToken.source();

// Definição das instâncias Axios
const panel = createAxiosInstance(facilita.panel);
const api = createAxiosInstance(facilita.api, {
  cancelToken: cancelTokenSource.token,
  withCredentials: true,
  headers: { 'goscore-token': facilita.gstoken }
});
const apiV2 = createAxiosInstance(facilita.apiV2, {
  cancelToken: cancelTokenSource.token,
  headers: { 'goscore-token': facilita.gstoken }
});
const apiOff = createAxiosInstance(facilita.apiOff);
const s3 = createAxiosInstance(facilita.s3Host);
const apiStage = createAxiosInstance(facilita.apiStage, {
  headers: {
    'goscore-token': facilita.gstoken,
    'api-instance': apiConfig ? apiConfig.instance : null
  }
});
const apiMicro = createAxiosInstance(facilita.apiMicro, {
  headers: {
    'goscore-token': facilita.gstoken,
    'api-instance': apiConfig && apiConfig.environment !== 'portainer'
      ? apiConfig.instance
      : 'portainer'
  }
});
const publicV2 = createAxiosInstance(facilita.publicV2);

// Início da implementação do cancelamento ao trocar de página
document.addEventListener('abortInPluginAxios', () => abortEvent());

let lastRequestUrl = '';
// Mapa para armazenar cancel tokens específicos
const cancelTokenMap = new Map();
// Função que verifica se existe alguma url que precisa continuar a execução mesmo mudando de página
function shouldContinueRequest(url) {
  const exceptions = ['/syncUnits', '/refreshToken'];
  return !exceptions.some(exception => url.includes(exception));
}

// Ele verifica se tem algum endpoint que é uma exceção que nao precisa ser cancelado
function abortEvent() {
  if (lastRequestUrl?.startsWith(facilita.apiV2) && !shouldContinueRequest(lastRequestUrl)) return;
  for (const [url, cancel] of cancelTokenMap.entries()) {
    if (shouldContinueRequest(url)) {
      cancel();
      cancelTokenMap.delete(url);
    }
  }
  cancelTokenSource.cancel();
  cancelTokenSource = axios.CancelToken.source();
  api.defaults.cancelToken = cancelTokenSource.token;
  apiV2.defaults.cancelToken = cancelTokenSource.token;
  apiMicro.defaults.cancelToken = cancelTokenSource.token;
  document.removeEventListener('abortInPluginAxios', () => abortEvent());
}

function generateUniqueKey(config) {
  const url = config.baseURL + config.url;
  const method = config.method;
  const params = JSON.stringify(config.params);
  const data = JSON.stringify(config.data);
  return `${method}:${url}?${params}&${data}`;
}// Fim da implementação ao trocar de  página

// Registra as instâncias Axios no Vue para uso global
Vue.use({
  install(Vue) {
    Vue.prototype.$panel = panel;
    Vue.prototype.$api = api;
    Vue.prototype.$apiV2 = apiV2;
    Vue.prototype.$apiOff = apiOff;
    Vue.prototype.$s3 = s3;
    Vue.prototype.$apiStage = apiStage;
    Vue.prototype.$apiMicro = apiMicro;
  }
});

// Variáveis para gerenciar o estado de refresh e fila de requisições que falharam
let isRefreshing = false;
let failedQueue = [];
let failedQueuev2 = [];

/**
 * Verifica se o token JWT está expirado
 * @param {string} token - O token JWT a ser verificado
 * @returns {boolean} - Retorna true se o token estiver expirado, caso contrário false
 */
const tokenExpired = (token) => token ? Date.now() >= helpers.methods.parseJwt(token).exp * 1000 : false;

/** Rejeita requisicoes com erro 403 e retorna erro
 * @param {Object} token - O token JWT a ser verificado
 * @param {boolean} isV2 - Verifica se e v1 ou v2 pra zerar os arrays da fila de tentativas
 */
const rejectQueue = (error, isV2 = false) => {
  failedQueue.forEach(promise => {
    promise.reject(`Erro ao atualizar o ${isV2 ? 'tokenV2' : 'tokenV1'}.`, error);
  })
  Vue.prototype.$toastr.Add({
    msg: 'Sua sessão expirou devido à inatividade. Por favor, recarregue a página para continuar.',
    type: 'custom'
  });
  isV2 ? failedQueuev2 = [] : failedQueue = [];
}
/**
 * Processa a fila de requisições que falharam durante o refresh token
 * Feito em funções separadas, apesar de parecidas, porque na mesma função estava com alguns conflitos
 * @param {Error|null} error - O erro que causou a falha
 * @param {string|null} token - O novo token atualizado, se disponível
 */
const processQueueV2 = async (error, token = null) => {
  failedQueuev2.forEach(promise => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });
  failedQueuev2 = [];
};
const processQueue = async (error, token = null) => {
  failedQueue.forEach(promise => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Cria os interceptadores da APIV2 pra cancelar as requisições quando troca de página e ainda tem requisições não finalizadas
 * @param {Object} axiosInstance - Qual instância a ser interceptada
 * @return {Promise} - Promise que cancela as requisições
 */
const setupCancelInterceptorV2 = (axiosInstance) => {
  axiosInstance.interceptors.request.use(config => {
    lastRequestUrl = config.baseURL + config.url;
    const source = axios.CancelToken.source();
    config.cancelToken = source.token;
    const uniqueKey = generateUniqueKey(config);
    cancelTokenMap.set(uniqueKey, source.cancel);
    return config;
  });

  axiosInstance.interceptors.response.use(
    response => {
      const uniqueKey = generateUniqueKey(response.config);
      cancelTokenMap.delete(uniqueKey);
      return response;
    },
    error => {
      // Verifica se o erro foi causado por um cancelamento
      if (axios.isCancel(error)) {
        return Promise.resolve(); // Resolve a promise para evitar que o erro seja propagado
      }
      // Para outros erros, remova o cancelamento do mapa e propague o erro
      if (error.config) {
        const uniqueKey = generateUniqueKey(error.config);
        cancelTokenMap.delete(uniqueKey);
        return Promise.resolve();
      }
      return Promise.reject(error); // Propaga o erro para outros tipos de falha
    }
  );
}


/**
 * Cria os interceptadores da API
 * @param {Object} axiosInstance - Qual instância a ser interceptada
 * @param {boolean} isV2 - Boolean pra sinalizar que é a V2
 */
const setupInterceptors = (axiosInstance, isV2 = false) => {
  axiosInstance.interceptors.request.use(
    (config) => {
      return handleRequest(config, isV2);
    },
    (error) => {
      Promise.reject(error)
    }
  );
  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => handleErrorResponse(error, isV2)
  );
};

/**
 * Busca um token na store e caso esteja expirado, atualiza e retorna um token
 * @returns {String} - Um token atualizado
 */
const getToken = async () => {
  let token = store?.getters?.tokenV2;
  const isRefreshing = store?.getters['auth/isRefreshingV2'];
  if (token && tokenExpired(token) && !isRefreshing) {
    token = await refreshingToken();
  }
  return token;
};

/**
 * Manipula a requisição antes de ser enviada, configurando headers de autenticação
 * @param {Object} config - A configuração da requisição Axios
 * @param {boolean} isV2 - Indica se o interceptor é para API V2
 * @returns {Promise<Object>} - A configuração modificada da requisição
 */
const handleRequest = async (config, isV2) => {
  if (isV2) {
    const tokenV2 = await getToken();
    if (tokenV2) {
      config.headers['Authorization'] = `Bearer ${tokenV2}`;
      if (process.env.VUE_APP_DEV_PORTAINER_PORT) {
        config.headers['port'] = parseInt(process.env.VUE_APP_DEV_PORTAINER_PORT)
      }
    }
  } else {
    let token = store?.getters?.token;
    if (token) {
      if (tokenExpired(token) && !store.getters.isRefreshing) {
        await store.dispatch('auth/setIsRefreshing', true);
      }
      config.headers['Authorization'] = `Bearer ${token}`;
    }
  }
  return config;
};

/**
 * Trata as requisições de erro 403 da api V2, garante que vão ser refeitas com um token atualizado
 * @param {Object} error - A configuração do Axios que contem as informações da URL e etc.
 * @returns {Promise<Object>} - A configuração modificada da requisição
 */
const handle403V2 = async (error) => {
  const originalRequest = error.config;
  const retryOriginalRequest = new Promise((resolve, reject) => {
    failedQueuev2.push({ resolve, reject });
  });

  if (store?.getters['auth/isRefreshingV2']) {
    return retryOriginalRequest.then(tokenV2 => {
      originalRequest.headers['Authorization'] = `Bearer ${tokenV2}`;
      return axios(originalRequest);
    }).catch(err => {
      return Promise.reject(err);
    });
  }

  try {
    const tokenV2 = await getToken();
    if (!store?.getters['auth/isRefreshingV2']) {
      await processQueueV2(null, tokenV2);
    }
    return retryOriginalRequest.then(() => {
      originalRequest.headers['Authorization'] = `Bearer ${tokenV2}`;
      return axios(originalRequest);
    });
  } catch (err) {
    rejectQueue(err, true);
    return Promise.reject(err);
  }
}

/**
 * Trata as requisições de erro 403 da api V1, garante que vão ser refeitas com um token atualizado
 * @param {Object} error - A configuração do Axios que contem as informações da URL e etc.
 * @returns {Promise<Object>} - A configuração modificada da requisição
 */
const handle403V1 = async (error) => {
  let originalRequest = error.config;
  if (!isRefreshing) {
    isRefreshing = true;
    let newToken = null;
    try {
      const refresh = error.response.status === 401 && error.response.data.error === 'refresh_token_invalid';
      let userToken = error.response.data['token'];
      let refreshToken = error.response.data['refresh_token'];
      if (!refresh && !(userToken && refreshToken)) throw error;
      if (refresh) {
        const config = {
          headers: {
            'refresh-token': store.getters.refreshToken
          }
        };
        let responseRefresh = await auth.refreshToken(config);
        userToken = responseRefresh.data.token;
        refreshToken = responseRefresh.data.refresh_token;
      }
      storage.set('user_token', userToken);
      storage.set('user_refresh_token', refreshToken);
      await store.dispatch('auth/setToken', userToken);
      await store.dispatch('auth/setRefreshToken', refreshToken);
      store.dispatch('auth/setIsRefreshing', false);
      await processQueue(null, userToken);
      originalRequest.headers['Authorization'] = `Bearer ${userToken}`;
      return axios(originalRequest);
    } catch (err) {
      await processQueue(err, null);
      Vue.prototype.$toastr.e('Erro ao atualizar o token. Tente novamente.');
    } finally {
      isRefreshing = false;
    }
  }
  return new Promise((resolve, reject) => {
    failedQueue.push({ resolve, reject });
  }).then(token => {
    originalRequest.headers['Authorization'] = `Bearer ${token}`;
    return axios(originalRequest);
  }).catch(err => {
    rejectQueue(err);
    return Promise.reject(err);
  });
}

/**
 * Timeout para evitar o redirecionamento múltiplo para a tela de erro de permissões,
 * quando houverem requisições concorrentes com permissões faltantes
 */
let unauthorizedTimeout = null

/**
 * Manipula a resposta da requisição, tratando erros de autenticação e permissão
 * Tem a tratativa pra não dar o erro caso seja na rota /authenticate, caso um usuário erre a senha, não deve aparecer mensagens de token.
 * @param {Object} error - O erro gerado pela requisição Axios
 * @param {boolean} isV2 - Indica se o interceptor é para API V2
 * @returns {Promise<Error>} - Retorna a rejeição da promessa com o erro
 */
const handleErrorResponse = async (error, isV2) => {
  if (!axios.isCancel(error)) {
    const statusCode = error?.response?.status;
    let permission = error?.response?.status === 401 && error.response.data
				? error.response.data.permission === 'denied'
				: false
    if(statusCode === 401 && permission ) {
        Vue.prototype.$toastr.e(error.response.data.msg)
        /** Seta no state do módulo vuex de autenticação a permissão que o usuário necessita */
        store.dispatch('auth/setUnauthorizedPermission', error.response.data.data.permission)
  
        if (router.currentRoute && router.currentRoute.name !== 'unauthorized' && router.currentRoute.name !== 'dashboard-managers') {
            /** Limpa o timeout de redirecionamento */
            clearTimeout(unauthorizedTimeout)

            /** Inicia o novo timeout */
            unauthorizedTimeout = setTimeout(() => {
                router.push('/erros/nao-autorizado')
            }, 500)
        }
    } else if (statusCode === 401 && error.config.url !== '/authenticate') {
        // Realiza logout, exibe mensagem e toast de erro
        await logout();
        Vue.prototype.$toastr.Add({
            msg: 'Sua sessão expirou devido à inatividade. Por favor, recarregue a página para continuar.',
            type: 'custom'
        });
    } else if (statusCode === 400 || statusCode === 422) {
      // Exibe toast de erro sem logout
      Vue.prototype.$toastr.e('Erro na solicitação. Verifique os dados e tente novamente.');
    } else if (statusCode === 403 && isV2) {
      return await handle403V2(error)
    } else if (statusCode === 403 && !isV2) {
      return await handle403V1(error)
    } else {
      setError500();
    }
  }
  return Promise.reject(error.response.data || error);
};

/**
 * Atualiza o token de autenticação ao detectar expiração da api V2
 * @param {Object} error - O erro original que disparou a necessidade de refresh
 * @param {boolean} isV2 - Indica se o refresh é para a API V2
 * @returns {Promise<string>} - O novo token atualizado
 */
const refreshingToken = async () => {
  try {
    await store.dispatch('auth/setIsRefreshingV2', true);
    const userToken = await auth.refreshTokenV2();
    storage.set('user_token_v2', userToken?.data?.access_token);
    await store.dispatch('auth/setTokenV2', userToken?.data?.access_token);
    await store.dispatch('auth/setIsRefreshingV2', false);
    return userToken?.data?.access_token;
  } catch (e) {
    await logout();
    Vue.prototype.$toastr.e(e.data?.msg || 'Erro ao atualizar o token.');
    throw e;
  }
};

/**
 * Realiza o logout do usuário e limpa os dados de autenticação
 */
const logout = async () => {
    store.dispatch('auth/clearUserData');
    storage.remove('user_token');
    storage.remove('user_token_v2');
    storage.remove('user_refresh_token');
    // retira o cookie para deslogar o cliente do painel
    await auth.logout()
    const targetOrigin = window?.parent?.location?.origin;
    // emite um evento que o a tela de interna de negocio esta ouvindo 
    // para desconectar do painel
    if ( targetOrigin ) {
        window?.parent?.postMessage({ url: "/sair" }, targetOrigin)
    }

};

/**
 * Redireciona o usuário para a página de erro 500
 */
const setError500 = () => {
  const error = router.history?.current?.meta?.iframe ? '/iframe/erro' : '/erros/erro';
  router.push(error).catch(() => { });
};

/**
 * Redireciona o usuário para a página de erro 400 (não autorizado)
 */
const setError400 = () => {
  const error = router.history?.current?.meta?.iframe ? '/iframe/unauthorized' : '/erros/nao-autorizado';
  router.push(error).catch(() => { });
};

// Configurar interceptors para cada instância do Axios
setupInterceptors(api);
setupInterceptors(apiV2, true);
setupInterceptors(apiStage);
setupInterceptors(apiMicro);

//Configura o interceptador pra API V2 com o objetivo de cancelar as requisições com a troca de página.
setupCancelInterceptorV2(apiV2);

// Exporta as instâncias do Axios
export {
  panel,
  api,
  apiV2,
  apiOff,
  s3,
  apiStage,
  apiMicro,
  publicV2
};